import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LifeBuoy, MessageSquare, Plus, Send, Check, X } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Billing", "Bug", "Feature Request", "AI Generation", "Account", "Other"];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const STATUS_LABELS = {
  open: { label: "Open", cls: "text-orange-400" },
  in_progress: { label: "In Progress", cls: "text-blue-400" },
  resolved: { label: "Resolved", cls: "text-green-400" },
  closed: { label: "Closed", cls: "text-white/40" },
};

export default function Support() {
  const { user } = useAuth();
  const isStaff = user?.role && user.role !== "client";
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "Bug", priority: "normal", message: "" });
  const [reply, setReply] = useState("");

  const load = async () => {
    const { data } = await api.get("/tickets");
    setTickets(data);
    if (selected) {
      const upd = data.find((t) => t.id === selected.id);
      setSelected(upd || null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const submit = async () => {
    if (!form.subject || !form.message) {
      toast.error("Subject and message required");
      return;
    }
    try {
      const { data } = await api.post("/tickets", form);
      setTickets([data, ...tickets]);
      setSelected(data);
      setCreating(false);
      setForm({ subject: "", category: "Bug", priority: "normal", message: "" });
      toast.success("Ticket submitted");
    } catch {
      toast.error("Failed");
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    await api.post(`/tickets/${selected.id}/reply`, { message: reply });
    setReply("");
    toast.success("Reply sent");
    await load();
  };

  const setStatus = async (status) => {
    if (!isStaff) return;
    await api.patch(`/tickets/${selected.id}/status`, { status });
    toast.success(`Status: ${status}`);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="support-root">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-orange-400/80 mb-2">Support</div>
          <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">
            {isStaff ? "Support Console" : "Help & Tickets"}
          </h1>
          <p className="text-white/50 mt-2 text-sm">
            {isStaff ? "Triage and resolve customer tickets across the platform." : "Open a ticket — our team replies within one business day."}
          </p>
        </div>
        {!isStaff && (
          <button
            onClick={() => setCreating(true)}
            data-testid="ticket-new-button"
            className="inline-flex items-center gap-2 brand-gradient px-5 py-3 rounded-lg text-sm font-semibold hover:opacity-90 glow-orange"
          >
            <Plus className="h-4 w-4" /> New ticket
          </button>
        )}
      </header>

      {creating && (
        <div className="glass rounded-2xl p-6 lg:p-8" data-testid="ticket-create-form">
          <h3 className="font-heading font-bold text-xl mb-4">New ticket</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Subject</label>
              <input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                data-testid="ticket-subject-input"
                className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Priority</label>
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="ticket-priority-select" className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm">
                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="md:col-span-3">
              <label className="text-xs uppercase tracking-[0.2em] text-white/40">Message</label>
              <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="ticket-message-input" className="mt-2 w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm" />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={submit} data-testid="ticket-submit" className="brand-gradient px-5 py-2.5 rounded-lg text-sm font-semibold hover:opacity-90">Submit</button>
            <button onClick={() => setCreating(false)} className="glass px-5 py-2.5 rounded-lg text-sm hover:bg-white/5">Cancel</button>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5 space-y-2 max-h-[640px] overflow-y-auto pr-1">
          {tickets.length === 0 && (
            <div className="glass rounded-2xl p-10 text-center">
              <LifeBuoy className="h-8 w-8 mx-auto text-white/30 mb-3" />
              <div className="text-white/50 text-sm">No tickets yet.</div>
            </div>
          )}
          {tickets.map((t) => (
            <button
              key={t.id}
              onClick={() => setSelected(t)}
              data-testid={`ticket-item-${t.id}`}
              className={`w-full text-left glass rounded-xl p-4 hover:bg-white/[0.04] transition border ${selected?.id === t.id ? "border-orange-500/50" : "border-white/5"}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[10px] uppercase tracking-[0.2em] ${STATUS_LABELS[t.status]?.cls || ""}`}>{STATUS_LABELS[t.status]?.label}</span>
                <span className="text-[10px] text-white/40 font-mono">{t.priority}</span>
              </div>
              <div className="font-semibold text-sm">{t.subject}</div>
              <div className="text-xs text-white/50 mt-1">
                {t.category} {isStaff && t.user_email ? `· ${t.user_email}` : ""}
              </div>
              <div className="text-xs text-white/40 mt-1 font-mono">
                {new Date(t.updated_at).toLocaleString()}
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-7">
          {selected ? (
            <div className="glass rounded-2xl p-6 lg:p-8 sticky top-6" data-testid="ticket-detail">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-heading text-2xl font-black">{selected.subject}</h3>
                  <div className="text-xs text-white/50 mt-1">
                    {selected.category} · priority {selected.priority} · <span className={STATUS_LABELS[selected.status]?.cls}>{STATUS_LABELS[selected.status]?.label}</span>
                  </div>
                </div>
                {isStaff && (
                  <select value={selected.status} onChange={(e) => setStatus(e.target.value)} data-testid="ticket-status-select" className="bg-white/5 border border-white/10 outline-none rounded-lg px-3 py-2 text-xs">
                    {Object.keys(STATUS_LABELS).map((s) => <option key={s} value={s}>{STATUS_LABELS[s].label}</option>)}
                  </select>
                )}
              </div>

              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                {(selected.messages || []).map((m) => (
                  <div key={m.id} className={`p-3 rounded-lg ${m.sender_role === "client" ? "bg-white/[0.03]" : "bg-orange-500/[0.08] border border-orange-500/20"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold">{m.sender_name}</span>
                      <span className="text-[10px] text-white/40 font-mono">{new Date(m.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed">{m.message}</p>
                  </div>
                ))}
              </div>

              {selected.status !== "closed" && (
                <div className="mt-5">
                  <textarea
                    rows={3}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder="Type your reply…"
                    data-testid="ticket-reply-input"
                    className="w-full bg-white/5 border border-white/10 outline-none rounded-lg px-4 py-3 text-sm"
                  />
                  <button
                    onClick={sendReply}
                    data-testid="ticket-reply-send"
                    className="mt-3 brand-gradient px-5 py-2.5 rounded-lg text-sm font-semibold inline-flex items-center gap-2 hover:opacity-90"
                  >
                    <Send className="h-4 w-4" /> Send reply
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="glass rounded-2xl p-10 text-center text-white/50">
              <MessageSquare className="h-8 w-8 mx-auto text-white/20 mb-3" />
              Select a ticket to view the conversation.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
